import { List as RefineList } from "@refinedev/antd";
import { Avatar, Flex, Space, Table, Tag, Tooltip, Typography } from "antd";
import { useList, useTranslate } from "@refinedev/core";
import { ColumnsType } from "antd/es/table";
import {
  CheckCircleIcon,
  PauseCircleIcon,
  ClockIcon,
} from "@phosphor-icons/react";
import { useOrganization } from "hooks/useOrganization/";
import { UserLicenseAssignmentResponse } from "types/";
import dayjs from "dayjs";
import { useAntTheme } from "hooks/useAntTheme";
import { radius } from "@libertify/ui";

export const SubscriptionUsersPage = () => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const { organization } = useOrganization({});
  const { data, isLoading } = useList<UserLicenseAssignmentResponse>({
    resource: `api/organizations/${organization?.id}/subscriptions/users`,
    queryOptions: {
      enabled: !!organization?.id,
    },
    pagination: {
      mode: "off",
    },
  });

  const columns: ColumnsType<UserLicenseAssignmentResponse> = [
    {
      title: t("pages.subscription.users.user", "User"),
      key: "user",
      sorter: (a, b) => {
        const nameA = (a.user_name || a.email).toLowerCase();
        const nameB = (b.user_name || b.email).toLowerCase();
        return nameA.localeCompare(nameB);
      },
      render: (_, record) => (
        <Space size="middle">
          <Avatar
            style={{
              backgroundColor: theme.colorPrimary,
              color: "white",
              minWidth: 30,
              minHeight: 30,
              fontSize: 14,
            }}
          >
            {record.user_name?.split(" ")?.[0]?.[0]?.toUpperCase()}
            {record.user_name?.split(" ")?.[1]?.[0]?.toUpperCase()}
          </Avatar>
          <Flex vertical>
            <Typography.Text>
              {record.user_name || record.email}
            </Typography.Text>
            <Typography.Text style={{ opacity: 0.8 }}>
              {record.email}
            </Typography.Text>

            {record.user_invite_id && !record.user_id && (
              <Tooltip
                title={t(
                  "pages.subscription.users.invitePending",
                  "Invite pending"
                )}
              >
                <Flex align="center" gap={4}>
                  <ClockIcon size={12} />
                  <Typography.Text style={{ opacity: 0.8, fontSize: 12 }}>
                    {t("pages.subscription.users.invitedAt", {
                      invitedAt: dayjs(record.user_invite_date).format("LL"),
                      inviteExpiry: dayjs(record.user_invite_expiry).format(
                        "LL"
                      ),
                    })}
                  </Typography.Text>
                </Flex>
              </Tooltip>
            )}
          </Flex>
        </Space>
      ),
    },
    {
      title: t("pages.subscription.users.status"),
      dataIndex: "status",
      key: "status",
      render: (status: UserLicenseAssignmentResponse["status"]) => (
        <Flex align="center" gap={5}>
          {status === "Active" ? (
            <Tag
              color="green"
              style={{
                border: 0,
                borderRadius: radius.pill,
                padding: 6,
                alignItems: "center",
                display: "flex",
                gap: 6,
              }}
              icon={
                <CheckCircleIcon
                  size={20}
                  weight="bold"
                  color={theme.colorSuccess}
                />
              }
            >
              {t("pages.subscription.users.status.active", "Active")}
            </Tag>
          ) : (
            <Tag
              color="warning"
              style={{
                border: 0,
                borderRadius: radius.pill,
                padding: 6,
                alignItems: "center",
                display: "flex",
                gap: 6,
              }}
              icon={
                <PauseCircleIcon
                  size={20}
                  weight="bold"
                  color={theme.colorWarning}
                />
              }
            >
              {status}
            </Tag>
          )}
        </Flex>
      ),
    },
    {
      title: t("pages.subscription.users.assignedDate", "Assigned Date"),
      dataIndex: "assigned_date",
      key: "assigned_date",
      sorter: (a, b) =>
        dayjs(a.assigned_date).unix() - dayjs(b.assigned_date).unix(),
      defaultSortOrder: "ascend",
      render: (date: string) => <span>{dayjs(date).format("LL")}</span>,
    },
  ];

  return (
    <RefineList
      title={t("subscriptionUsers", { ns: "page-titles" })}
      headerButtons={<></>}
    >
      <Table
        dataSource={data?.data || []}
        columns={columns}
        pagination={false}
        loading={isLoading}
        rowKey={(record) => record.email}
      />
    </RefineList>
  );
};
